import { useState, useEffect, useCallback, useRef } from 'react';
import { CameraSet } from '../../types/canvision.types';

interface UseVideoWallRotationOptions {
  sets: CameraSet[];
  dwellTimeSeconds: number;
  enabled: boolean;
}

interface UseVideoWallRotationResult {
  currentSetIndex: number;
  currentSet: CameraSet | null;
  isPlaying: boolean;
  timeRemaining: number;
  play: () => void;
  pause: () => void;
  next: () => void;
  previous: () => void;
  goToSet: (index: number) => void;
}

export function useVideoWallRotation({
  sets,
  dwellTimeSeconds,
  enabled,
}: UseVideoWallRotationOptions): UseVideoWallRotationResult {
  const [currentSetIndex, setCurrentSetIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(dwellTimeSeconds);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Reset time remaining when dwell time changes
  useEffect(() => {
    setTimeRemaining(dwellTimeSeconds);
  }, [dwellTimeSeconds]);

  // Reset index if it's out of bounds
  useEffect(() => {
    if (sets.length > 0 && currentSetIndex >= sets.length) {
      setCurrentSetIndex(0);
    }
  }, [sets.length, currentSetIndex]);

  // Stop playback if disabled or no sets
  useEffect(() => {
    if (!enabled || sets.length === 0) {
      setIsPlaying(false);
    }
  }, [enabled, sets.length]);

  // Main rotation timer
  useEffect(() => {
    if (!isPlaying || sets.length <= 1) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    intervalRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          // Advance to next set
          setCurrentSetIndex((idx) => (idx + 1) % sets.length);
          return dwellTimeSeconds;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isPlaying, sets.length, dwellTimeSeconds]);

  const play = useCallback(() => {
    if (sets.length > 1) {
      setIsPlaying(true);
      setTimeRemaining(dwellTimeSeconds);
    }
  }, [sets.length, dwellTimeSeconds]);

  const pause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const next = useCallback(() => {
    if (sets.length > 0) {
      setCurrentSetIndex((idx) => (idx + 1) % sets.length);
      setTimeRemaining(dwellTimeSeconds);
    }
  }, [sets.length, dwellTimeSeconds]);

  const previous = useCallback(() => {
    if (sets.length > 0) {
      setCurrentSetIndex((idx) => (idx - 1 + sets.length) % sets.length);
      setTimeRemaining(dwellTimeSeconds);
    }
  }, [sets.length, dwellTimeSeconds]);

  const goToSet = useCallback(
    (index: number) => {
      if (index >= 0 && index < sets.length) {
        setCurrentSetIndex(index);
        setTimeRemaining(dwellTimeSeconds);
      }
    },
    [sets.length, dwellTimeSeconds]
  );

  const currentSet = sets.length > 0 ? sets[currentSetIndex] || null : null;

  return {
    currentSetIndex,
    currentSet,
    isPlaying,
    timeRemaining,
    play,
    pause,
    next,
    previous,
    goToSet,
  };
}
